import { useState } from "react";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import {
  Handshake,
  ClipboardList,
  Server,
  Wifi,
  Wallet,
  TicketPercent,
  Wrench,
  Navigation,
  Send,
  Copy,
  MessageCircle,
  CheckCircle2,
  RotateCcw,
  MapPin,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/15 bg-[#050914]/60 px-4 py-3 text-sm text-white placeholder:text-slate-500 transition-colors duration-200 focus:border-cyan-400/60 focus:outline-none";
const labelCls = "mb-2 block text-xs font-bold uppercase tracking-wider text-blue-200/70";

const BENEFITS = [
  {
    Icon: Server,
    title: "Tempat Mini Server",
    desc: "Rumahmu menjadi titik Mini Server FASNET FIBER di lingkunganmu.",
  },
  {
    Icon: Wifi,
    title: "Gratis Internet",
    desc: "Internet FASNET gratis selama kamu menjadi Mitra aktif.",
  },
  {
    Icon: Wallet,
    title: "Payment Point",
    desc: "Komisi Rp 10.000 per konsumen yang bayar tagihan di tempatmu.",
  },
  {
    Icon: TicketPercent,
    title: "Jual Voucher WiFi",
    desc: "Komisi 25% dari setiap harga voucher yang berhasil dijual.",
  },
  {
    Icon: Wrench,
    title: "Menjadi Teknisi Lokal FASNET FIBER",
    desc: "Berkesempatan menjadi teknisi resmi FASNET FIBER di wilayahmu sendiri.",
  },
];

export default function MitraPage() {
  const [form, setForm] = useState({ full_name: "", phone_number: "", address: "", notes: "" });
  const [pos, setPos] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const useGps = () => {
    if (!navigator.geolocation) return toast.error("Perangkat tidak mendukung GPS");
    toast.info("Mengambil lokasi GPS...");
    navigator.geolocation.getCurrentPosition(
      (g) => {
        setPos([g.coords.latitude, g.coords.longitude]);
        toast.success("Lokasi GPS ditemukan!");
      },
      () => toast.error("Gagal mengambil lokasi. Izinkan akses lokasi di browser/HP Anda."),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const copyCoords = () => {
    if (!pos) return;
    navigator.clipboard.writeText(`${pos[0]}, ${pos[1]}`);
    toast.success("Koordinat disalin");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!pos)
      return toast.error("Tekan tombol 'Kirim Lokasi Saya' untuk menandai lokasi mini server dulu ya");
    setBusy(true);
    try {
      const res = await fetch(`${API}/mitra-applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, latitude: pos[0], longitude: pos[1] }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Gagal mengirim pengajuan");
      setDone(data);
      toast.success("Pengajuan mitra berhasil dicatat!");
    } catch (err) {
      toast.error(err.message || "Terjadi kesalahan, coba lagi");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setDone(null);
    setPos(null);
    setForm({ full_name: "", phone_number: "", address: "", notes: "" });
  };

  return (
    <div className="min-h-screen bg-[#050914] font-sans text-slate-100 antialiased">
      <Navbar />
      <main data-testid="mitra-page" className="relative overflow-hidden pb-20 pt-28 sm:pt-32">
        <div className="pointer-events-none absolute -left-32 top-20 h-[420px] w-[420px] rounded-full bg-[#2563EB]/12 blur-[160px]" />
        <div className="pointer-events-none absolute -right-32 top-96 h-[380px] w-[380px] rounded-full bg-[#F04E23]/10 blur-[160px]" />

        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-cyan-400">
              <Handshake className="h-4 w-4" /> PROGRAM KEMITRAAN
            </p>
            <h1 className="mt-4 max-w-3xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              MAU Jadi Mitra <span className="text-chameleon">FASNET FIBER?</span>
            </h1>
            <p className="mt-4 max-w-2xl text-base text-slate-300">
              Rumahmu bisa jadi sumber penghasilan tambahan. Ajukan diri jadi Mitra dan nikmati 5
              benefit ini:
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            data-testid="mitra-requirement"
            className="mt-8 flex max-w-3xl items-center gap-3 rounded-2xl border border-[#F04E23]/40 bg-[#F04E23]/10 px-5 py-4"
          >
            <ClipboardList className="h-6 w-6 shrink-0 text-[#FF6B35]" />
            <p className="text-sm text-slate-200">
              <span className="font-bold text-white">Syarat menjadi Mitra FASNET FIBER:</span>{" "}
              Daftarkan <span className="font-extrabold text-[#FF6B35]">30 Rumah Warga</span> yang
              akan berlangganan FASNET FIBER.
            </p>
          </motion.div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {BENEFITS.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.08, duration: 0.55 }}
                data-testid={`mitra-benefit-${i}`}
                className="rounded-2xl border border-white/10 bg-[#0A1124]/80 p-5 backdrop-blur-xl transition-colors duration-300 hover:border-cyan-400/40"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#2563EB]/15">
                  <b.Icon className="h-5 w-5 text-cyan-400" />
                </span>
                <h3 className="mt-4 font-display text-base font-bold text-white">{b.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-400">{b.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="mt-12 rounded-3xl border border-[#2563EB]/30 bg-gradient-to-br from-[#0B1B4D]/90 to-[#071233]/90 p-6 backdrop-blur-xl sm:p-10"
          >
            {done ? (
              <div data-testid="mitra-success-panel" className="py-8 text-center">
                <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" />
                <h3 className="mt-4 font-display text-2xl font-bold text-white">
                  Pengajuan Mitra Diterima!
                </h3>
                <p className="mt-2 text-sm text-slate-400">Nomor pengajuan Anda:</p>
                <p
                  data-testid="mitra-ref-id"
                  className="mt-1 font-mono text-2xl font-bold text-cyan-400"
                >
                  {done.id}
                </p>
                <p className="mx-auto mt-4 max-w-sm text-sm text-slate-400">
                  Teruskan detail pengajuan ke CS kami agar verifikasi & survei lokasi lebih cepat:
                </p>
                <a
                  data-testid="mitra-forward-wa"
                  href={done.whatsapp_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 font-bold text-white transition-colors duration-200 hover:bg-emerald-400"
                >
                  <MessageCircle className="h-5 w-5" /> Teruskan ke WhatsApp CS
                </a>
                <div>
                  <button
                    data-testid="mitra-btn-reset"
                    onClick={reset}
                    className="mt-5 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors duration-200 hover:text-white"
                  >
                    <RotateCcw className="h-4 w-4" /> Buat Pengajuan Baru
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="font-display text-2xl font-extrabold text-white sm:text-3xl">
                  Form Pengajuan Mitra
                </h2>
                <p className="mt-3 max-w-2xl text-sm text-slate-300">
                  Isi data di bawah — tim FASNET akan menghubungi via WhatsApp untuk verifikasi &
                  survei lokasi mini server.
                </p>
                <form onSubmit={submit} data-testid="mitra-form" className="mt-8 space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className={labelCls}>Nama Lengkap</label>
                      <input
                        data-testid="mitra-input-name"
                        required
                        minLength={3}
                        value={form.full_name}
                        onChange={set("full_name")}
                        placeholder="Nama sesuai KTP"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Nomor HP / WhatsApp</label>
                      <input
                        data-testid="mitra-input-phone"
                        required
                        type="tel"
                        minLength={8}
                        value={form.phone_number}
                        onChange={set("phone_number")}
                        placeholder="0812xxxxxxxx"
                        className={inputCls}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Alamat Rumah (Lokasi Mini Server)</label>
                    <textarea
                      data-testid="mitra-input-address"
                      required
                      minLength={5}
                      rows={2}
                      value={form.address}
                      onChange={set("address")}
                      placeholder="Jalan, RT/RW, patokan rumah..."
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Catatan (opsional)</label>
                    <input
                      data-testid="mitra-input-notes"
                      value={form.notes}
                      onChange={set("notes")}
                      placeholder="Mis. daya listrik rumah, perkiraan jumlah tetangga yang berminat"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" /> Titik Lokasi Mini Server
                      </span>
                    </label>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <button
                        data-testid="mitra-btn-gps"
                        type="button"
                        onClick={useGps}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#3b82f6]"
                      >
                        <Navigation className="h-4 w-4" /> Kirim Lokasi Saya
                      </button>
                      <div
                        data-testid="mitra-coords-display"
                        className="flex flex-1 items-center justify-between gap-2 rounded-xl border border-white/15 bg-[#050914]/60 px-4 py-3 font-mono text-xs"
                      >
                        <span className={pos ? "text-cyan-300" : "text-slate-500"}>
                          {pos
                            ? `${pos[0].toFixed(6)}, ${pos[1].toFixed(6)}`
                            : "Belum ada titik — tekan tombol di samping"}
                        </span>
                        {pos && (
                          <button
                            data-testid="mitra-btn-copy-coords"
                            type="button"
                            onClick={copyCoords}
                            className="text-slate-400 transition-colors duration-200 hover:text-white"
                            aria-label="Salin koordinat"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    data-testid="mitra-submit"
                    type="submit"
                    disabled={busy}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F04E23] px-8 py-3.5 font-bold text-white transition-colors duration-200 hover:bg-[#FF6B35] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Send className="h-4 w-4" />
                    {busy ? "Mengirim Pengajuan..." : "Ajukan Diri Jadi Mitra"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </main>
      <Footer />
      <Toaster theme="dark" position="top-center" richColors />
    </div>
  );
}
